import { Spacer } from "@/components/Spacer";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function Section09({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="w-full flex flex-col items-center bg-[#FFFFFF] desktop:py-[100px] desktop:px-[40px] py-[60px] px-[20px]">
      <div className="desktop:w-[960px] w-[342px] flex flex-col items-center">
        <div
          className="font-pre-bold text-[#001122] desktop:text-[32px] text-[24px] leading-[1.2em] whitespace-pre-line"
          style={{ letterSpacing: "-0.02px" }}
        >
          자주 묻는 질문
        </div>

        <Spacer height={isMobile ? 40 : 60} />
        <div className="w-full flex flex-col items-center gap-[12px]">
          <Question
            title="헬시어는 어떤 서비스인가요?"
            description={`헬시어(Healthier)는 건강이 걱정될 때, 가장 먼저 찾게 되는 AI 건강 파트너입니다. 헬시어는 사용자의 증상, 건강 정보, 생활 습관 등을 바탕으로 개인 맞춤형 건강 분석과 질병 예측을 제공하는 AI 기반 건강 관리 앱입니다.


이런 분들께 추천드려요.
- 병원 가기 전, 내 증상이 어떤 문제인지 알고 싶은 분
- 나에게 맞는 건강 관리가 필요하지만, 방법을 잘 모르겠는 분
- 바쁜 일상 속에서도 건강을 챙기고 싶은 2030 직장인`}
            isMobile={isMobile}
          />
          <Question
            title="건강 분석 결과는 어떻게 생셩되나요?"
            description={`사용자가 입력한 문진 내용과 건강 데이터를 기반으로 AI 알고리즘이 분석하여 결과를 제공합니다. 



의학 데이터를 기반으로 동작하지만, 정식 진단이나 처방이 아니며 참고용으로만 사용해주세요. 

증상이 지속되거나 심한 경우 반드시 전문의를 찾으시기 바랍니다.`}
            isMobile={isMobile}
          />
          <Question
            title="제 건강 정보는 안전하게 보관되나요?"
            description={`헬시어는 사용자의 건강 정보를 AES-256 암호화 및 TLS 보안 통신으로 안전하게 보호합니다.


모든 데이터는 국내 개인정보보호법 및 의료정보 관련 법령을 철저히 준수해 저장·관리됩니다.

또한, 접근 권한 통제 및 보안 모니터링 시스템을 통해 외부 유출을 방지하고 있습니다.`}
            isMobile={isMobile}
          />
          <Question
            title="헬시어를 이용하는데 비용이 발생하나요?"
            description={`헬시어는 기본적으로 무료로 이용하실 수 있습니다.

건강이 걱정될 때 누구나 쉽게 사용할 수 있도록, AI 증상 분석과 병원 안내 기능을 포함한 

주요 서비스를 모두 무료로 제공하고 있습니다.헬시어는 건강이 필요한 순간, 누구나 부담 없이 접근할 수 있는 서비스를 지향합니다.

언제든지 편하게 이용해보세요!`}
            isMobile={isMobile}
          />
          <Question
            title="고객센터 운영 시간은 어떻게 되나요?"
            description={`평일 오전 10시부터 오후 6시까지 운영되며, 

카카오톡 채널 "헬시어(Healthier)" 혹은 

앱 내 1:1 문의게시판, 또는 이메일(be@healthier.kr)로 문의 주시면 빠르게 답변드립니다.`}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
}

const Question = ({
  title,
  description,
  isMobile,
}: {
  title: string;
  description?: string;
  isMobile: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const springTransition = {
    type: "spring",
    stiffness: 100,
    damping: 15,
    mass: 0.8,
  };

  return (
    <div
      onClick={handleClick}
      className="w-full flex flex-col items-center p-[24px] bg-[#F7F8FA] rounded-[16px]"
    >
      <div className="w-full flex flex-row items-center justify-between">
        <div
          className="font-pre-bold text-[#343434] desktop:text-[16px] leading-[140%]"
          style={{ letterSpacing: "-0.5px" }}
        >
          {title}
        </div>
        <motion.img
          src={"assets/images/chevron-down.png"}
          alt="arrow-down"
          style={{ width: "24px", height: "24px" }}
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={springTransition}
        />
      </div>
      <AnimatePresence mode="wait">
        {description && isOpen && (
          <motion.div
            className="w-full text-start"
            initial={{
              height: 0,
              opacity: 0,
              overflow: "hidden",
              marginTop: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
              overflow: "visible",
              marginTop: isMobile ? 8 : 20,
            }}
            exit={{ height: 0, opacity: 0, overflow: "hidden", marginTop: 0 }}
            transition={{
              ...springTransition,
              height: { duration: 0.25 },
              opacity: { duration: 0.15 },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => (
                  <p
                    className="font-pre-regular text-[#343434] text-[16px] leading-[150%]"
                    style={{ letterSpacing: "-0.5px" }}
                    {...props}
                  />
                ),
                h1: ({ node, ...props }) => (
                  <h1
                    className="font-pre-bold text-[#001122] text-[24px] leading-[1.3em] mt-3 mb-2"
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className="font-pre-bold text-[#001122] text-[20px] leading-[1.3em] mt-3 mb-2"
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className="font-pre-bold text-[#001122] text-[18px] leading-[1.3em] mt-3 mb-2"
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc ml-5 my-2" {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li
                    className="font-pre-regular text-[#343434] text-[16px] leading-[150%] my-1"
                    style={{ letterSpacing: "-0.5px" }}
                    {...props}
                  />
                ),
                strong: ({ node, ...props }) => (
                  <strong className="font-pre-bold text-[#343434]" {...props} />
                ),
                a: ({ node, ...props }) => (
                  <a
                    className="font-pre-regular text-[#0066CC] underline"
                    {...props}
                  />
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="border-l-4 border-[#DDDDDD] pl-4 my-3 italic text-[#555555]"
                    {...props}
                  />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto w-full my-3">
                    <table className="w-full border-collapse" {...props} />
                  </div>
                ),
                thead: ({ node, ...props }) => (
                  <thead className="bg-[#F0F0F0]" {...props} />
                ),
                tbody: ({ node, ...props }) => <tbody {...props} />,
                tr: ({ node, ...props }) => (
                  <tr className="border-b border-[#DDDDDD]" {...props} />
                ),
                th: ({ node, ...props }) => (
                  <th
                    className="font-pre-bold text-[#343434] p-2 text-left"
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td
                    className="font-pre-regular text-[#343434] p-2"
                    {...props}
                  />
                ),
              }}
            >
              {description}
            </ReactMarkdown>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
